import { StringCase, transformCase } from '@attain-apps/string-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Fn } from './fn'

export enum Key {
  // Lookups
  GetAncestorId = '@^', // TODO: maybe delete when GetAttribute is generalized
  GetAttribute = '@.', // TODO: maybe this should be generalized; e.g. { "@.": "purposes" }, { "@.": "parent" }, { "@.": "creationDate" }, { "@.": "isLocked" }
  GetRecord = '@.*', // TODO: add option to select certain attributes, and add option that allows us to return something like { __type__, attributeId, recordId, value }[] where __type__ is used to indicate what type of special object this is (in this case an attribute for a form, but in other cases this could be a dashboard reference or something else)
  GetSubquery = '@', // TODO: this should be generalized (mostly renamed - maybe GetResult) to work for GetSubquery or GetAggregates
  GetProperty = '@>',
  GetVariable = '@$', // TODO: add variable for $selection so we can reference selection in filter
  GetModelInfo = '@_', // TODO: rename to GetModelInfo
  // Objects/Lists
  CreateObject = '{}',
  CreateTuple = '[]',
  ObjectToList = '{}>[]',
  ListToObject = '[]>{}',
  Map = '->',
  Concatenate = '++', // TODO: maybe combine with Add
  Unique = 'uniq',
  Inject = '`',
  // Logic
  Not = 'not',
  And = 'and',
  Or = 'or',
  Some = 'some',
  Every = 'every',
  If = 'if',
  Coalesce = '??',
  // Comparisons
  In = 'in',
  Equals = '=',
  NotEquals = '!=',
  GreaterThan = '>',
  GreaterThanOrEqualTo = '>=',
  LessThan = '<',
  LessThanOrEqualTo = '<=',
  IsSimilar = 'isSimilar',
  // Math
  Add = '+',
  Subtract = '-',
  Multiply = '*',
  Divide = '/',
  Modulo = '%',
  Exponent = '^',
  // Aggregation
  Min = 'min',
  Max = 'max',
  Count = '#',
  // Other
  FormatNumber = 'fmt.num',
  Score = 'score',
  Similarity = 'similarity',
}
// Needs to be before namespace is defined
const KEYS: ReadonlySet<Key> = new Set(Object.values(Key) as Key[])
export namespace Key {
  export const ALL: readonly Key[] = Array.from(KEYS)
  export const isKey = TypeChecker.Factory.oneOf(KEYS).usingAlias('Fn.Key')
  export const assertIsKey: TypeAsserter<Key> = TypeAsserter.Factory.from(isKey)

  /**
   * Anything we can extract a {@link Key} {@link From}
   */
  export type Source = Key | Alias | Fn

  /**
   * {@link Key} corresponding to the given {@link Fn} or {@link Alias}
   */
  export type From<TSource extends Source> = Satisfies<
    TSource extends Key
      ? TSource
      : TSource extends Alias
        ? Extract<
            (typeof _ALIAS_LOOKUP)[TSource],
            // NOTE: not clear why this is needed
            (typeof _ALIAS_LOOKUP)[Alias]
          >
        : // NOTE: this part is needed only because Fn is all Partial, which means anything gets picked up, including `string`
          TSource extends Primitive
          ? never
          : From<Extract<globalThis.Key.In.Union<TSource>, Key | Alias>>,
    Key
  >

  /**
   * Extracts the {@link Key} that corresponds to the given {@link Fn} or
   * {@link Alias}, if possible
   */
  export function from<TSource extends Source>(
    fn: Exclude<TSource, Primitive>,
  ): From<TSource>
  export function from(fn: unknown): Key | undefined
  export function from(source: unknown): Key | undefined {
    if (!source) {
      return undefined
    }
    if (isKey(source)) {
      return source
    }
    if (typeof source === 'string') {
      return (_ALIAS_LOOKUP as Generalize.Lookup.From<typeof _ALIAS_LOOKUP>)[
        // Standardizes the case (in case someone uses 'Add' instead of 'add')
        transformCase(source, StringCase.Camel)
      ]
    }
    return Object.entries(source)
      .filter(([, value]) => value !== undefined)
      .map(([key]) => from(key))
      .find(TypeChecker.isNotNullish.raw)
  }
}

/* -------------------------------------------------------------------------- */
/*                                   Aliases                                  */
/* -------------------------------------------------------------------------- */

const _ALIAS_LOOKUP = {
  add: Key.Add,
  attr: Key.GetAttribute,
  attribute: Key.GetAttribute,
  coalesce: Key.Coalesce,
  concat: Key.Concatenate,
  concatenate: Key.Concatenate,
  count: Key.Count,
  createList: Key.CreateTuple,
  createObject: Key.CreateObject,
  createTuple: Key.CreateTuple,
  dedupe: Key.Unique,
  deduplicate: Key.Unique,
  divide: Key.Divide,
  eq: Key.Equals,
  equals: Key.Equals,
  exponent: Key.Exponent,
  formatNumber: Key.FormatNumber,
  getAncestorId: Key.GetAncestorId,
  getAttribute: Key.GetAttribute,
  getModelInfo: Key.GetModelInfo,
  getProperty: Key.GetProperty,
  getRecord: Key.GetRecord,
  getSubquery: Key.GetSubquery,
  getVariable: Key.GetVariable,
  greaterThan: Key.GreaterThan,
  greaterThanOrEqualTo: Key.GreaterThanOrEqualTo,
  inject: Key.Inject,
  length: Key.Count,
  lessThan: Key.LessThan,
  lessThanOrEqualTo: Key.LessThanOrEqualTo,
  list: Key.CreateTuple,
  listToObject: Key.ListToObject,
  literal: Key.Inject,
  mod: Key.Modulo,
  modulo: Key.Modulo,
  multiply: Key.Multiply,
  notEquals: Key.NotEquals,
  object: Key.CreateObject,
  objectToList: Key.ObjectToList,
  size: Key.Count,
  subquery: Key.GetSubquery,
  subtract: Key.Subtract,
  sum: Key.Add,
  tuple: Key.CreateTuple,
  unique: Key.Unique,
  var: Key.GetVariable,
  variable: Key.GetVariable,
  // NOTE: this `satisfies` check prevents us from accidentally including a
  // `Key` here
} as const satisfies Partial<Record<Key, never>> & Partial<Record<string, Key>>

export type Alias = keyof typeof _ALIAS_LOOKUP
export namespace Alias {
  export const ALL: readonly Alias[] = Object.keys(_ALIAS_LOOKUP) as Alias[]
  export const isAlias = TypeChecker.Factory.oneOf(ALL)
  export const assertIsAlias: TypeAsserter<Alias> =
    TypeAsserter.Factory.from(isAlias)
}
